import React, { useState, useMemo, useRef } from 'react';
import {
  useGetFraudesQuery, useCreateFraudeMutation, useChangerStatutFraudeMutation,
  useDeleteFraudeMutation, useGetStatistiquesFraudesQuery, CasFraude,
  useAjouterPreuveFraudeMutation, useSupprimerPreuveFraudeMutation,
} from '../../services/fraudesApi';
import { useGetEtudiantsQuery } from '../../services/etudiantsApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import {
  AlertTriangle, Search, Plus, X, ChevronDown, ChevronRight, Shield,
  FileText, Clock, CheckCircle2, XCircle, Eye, Edit, Trash2, Filter,
  Printer, RefreshCw, Upload, Image as ImageIcon, Download, Camera,
} from 'lucide-react';

const TYPE_LABELS: Record<string, { label: string; color: string }> = {
  double_composition: { label: 'Double composition', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  copie_examen: { label: 'Copie pendant examen', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' },
  usurpation_identite: { label: 'Usurpation identite', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
  plagiat: { label: 'Plagiat', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  falsification_document: { label: 'Falsification', color: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300' },
  substitution_copie: { label: 'Substitution copie', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300' },
  communication_interdite: { label: 'Communication', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  materiel_interdit: { label: 'Materiel interdit', color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300' },
  autre: { label: 'Autre', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
};

const STATUT_LABELS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  detecte: { label: 'Detecte', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: <AlertTriangle size={12} /> },
  en_instruction: { label: 'En instruction', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: <FileText size={12} /> },
  confirme: { label: 'Confirme', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300', icon: <CheckCircle2 size={12} /> },
  sanctionne: { label: 'Sanctionne', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: <Shield size={12} /> },
  appel: { label: 'En appel', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300', icon: <Clock size={12} /> },
  classe: { label: 'Classe sans suite', color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400', icon: <XCircle size={12} /> },
  clos: { label: 'Clos', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: <CheckCircle2 size={12} /> },
};

const GRAVITE_COLORS: Record<string, string> = {
  mineure: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  moyenne: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  grave: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  tres_grave: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const GestionFraudes: React.FC = () => {
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const [search, setSearch] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [filtreType, setFiltreType] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedCas, setSelectedCas] = useState<CasFraude | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  const { data: fraudes = [], isLoading, refetch } = useGetFraudesQuery({
    search: search || undefined,
    statut: filtreStatut || undefined,
    type_fraude: filtreType || undefined,
    annee_academique: anneeActive?.libelle || undefined,
  });
  const { data: stats } = useGetStatistiquesFraudesQuery({ annee_academique: anneeActive?.libelle || '' });
  const [createFraude, { isLoading: creating }] = useCreateFraudeMutation();
  const [changerStatut] = useChangerStatutFraudeMutation();
  const [deleteFraude] = useDeleteFraudeMutation();
  const [ajouterPreuve, { isLoading: uploadingPreuve }] = useAjouterPreuveFraudeMutation();
  const [supprimerPreuve] = useSupprimerPreuveFraudeMutation();
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [preuveType, setPreuveType] = useState('copie_scannee');
  const [preuveTitre, setPreuveTitre] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadPreuve = async (file: File) => {
    if (!selectedCas) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('Le fichier dépasse 10 Mo');
      return;
    }
    const formData = new FormData();
    formData.append('fichier', file);
    formData.append('type_preuve', preuveType);
    formData.append('titre', preuveTitre || file.name);
    try {
      await ajouterPreuve({ id: selectedCas.id, formData }).unwrap();
      setPreuveTitre('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      // Recharger le cas pour voir la nouvelle preuve
      refetch();
      // Re-sélectionner le cas mis à jour après refetch
      setTimeout(() => {
        const updated = fraudes.find((f: any) => f.id === selectedCas.id);
        if (updated) setSelectedCas(updated);
      }, 300);
    } catch (err: any) {
      alert(err?.data?.error || 'Erreur lors de l\'upload');
    }
  };

  const handleDeletePreuve = async (preuveId: number) => {
    if (!selectedCas) return;
    if (!window.confirm('Supprimer cette preuve ?')) return;
    try {
      await supprimerPreuve({ fraudeId: selectedCas.id, preuveId }).unwrap();
      refetch();
      setTimeout(() => {
        const updated = fraudes.find((f: any) => f.id === selectedCas.id);
        if (updated) setSelectedCas(updated);
      }, 300);
    } catch (err: any) {
      alert(err?.data?.error || 'Erreur de suppression');
    }
  };

  // Formulaire
  const [form, setForm] = useState({
    etudiant: '',
    matiere_nom: '', matiere_code: '', semestre: '',
    type_fraude: 'double_composition', gravite: 'moyenne',
    date_examen: '', description: '', circonstances: '',
    code_copie_1: '', code_copie_2: '', pieces_jointes: '', notes_internes: '',
  });
  const [preuvesAUploader, setPreuvesAUploader] = useState<Array<{ file: File; type: string; titre: string }>>([]);
  const formFileInputRef = useRef<HTMLInputElement>(null);

  // Recherche etudiant
  const { data: etudiants = [] } = useGetEtudiantsQuery();
  const [etudiantSearch, setEtudiantSearch] = useState('');
  const [showEtudiantDropdown, setShowEtudiantDropdown] = useState(false);
  const etudiantsFiltres = useMemo(() => {
    if (!etudiantSearch || etudiantSearch.length < 2) return [];
    const q = etudiantSearch.toLowerCase();
    return etudiants.filter((e: any) =>
      e.matricule?.toLowerCase().includes(q) || e.nom?.toLowerCase().includes(q) || e.prenoms?.toLowerCase().includes(q)
    ).slice(0, 10);
  }, [etudiants, etudiantSearch]);

  const handleCreate = async () => {
    if (!form.etudiant || !form.type_fraude || !form.description) {
      alert('Etudiant, type de fraude et description sont requis');
      return;
    }
    try {
      const created: any = await createFraude({
        etudiant: parseInt(form.etudiant) as any,
        matiere_nom: form.matiere_nom,
        matiere_code: form.matiere_code,
        annee_academique: anneeActive?.libelle || '2025-2026',
        semestre: form.semestre,
        type_fraude: form.type_fraude,
        gravite: form.gravite,
        date_examen: form.date_examen || undefined,
        description: form.description,
        circonstances: form.circonstances,
        code_copie_1: form.code_copie_1,
        code_copie_2: form.code_copie_2,
        pieces_jointes: form.pieces_jointes,
        notes_internes: form.notes_internes,
      } as any).unwrap();

      // Uploader toutes les preuves attachées
      if (preuvesAUploader.length > 0 && created?.id) {
        for (const p of preuvesAUploader) {
          const fd = new FormData();
          fd.append('fichier', p.file);
          fd.append('type_preuve', p.type);
          fd.append('titre', p.titre || p.file.name);
          try {
            await ajouterPreuve({ id: created.id, formData: fd }).unwrap();
          } catch (err: any) {
            console.error('Erreur upload preuve:', err);
          }
        }
      }

      setShowForm(false);
      setForm({ etudiant: '', matiere_nom: '', matiere_code: '', semestre: '', type_fraude: 'double_composition', gravite: 'moyenne', date_examen: '', description: '', circonstances: '', code_copie_1: '', code_copie_2: '', pieces_jointes: '', notes_internes: '' });
      setPreuvesAUploader([]);
      setEtudiantSearch('');
      refetch();
    } catch (err: any) {
      alert(err?.data?.error || 'Erreur lors de la creation');
    }
  };

  const handleChangerStatut = async (cas: CasFraude, statut: string) => {
    const commentaire = prompt(`Commentaire pour le changement vers "${STATUT_LABELS[statut]?.label}" :`);
    if (commentaire === null) return;
    try {
      await changerStatut({ id: cas.id, statut, commentaire }).unwrap();
      refetch();
    } catch { /* ignore */ }
  };

  const handleDelete = async (cas: CasFraude) => {
    if (!window.confirm(`Supprimer le cas ${cas.reference} ?`)) return;
    await deleteFraude(cas.id).unwrap();
    refetch();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
            <Shield size={28} className="text-red-600" /> Gestion des Fraudes
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Enregistrement, suivi et historique des cas de fraude
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowForm(true)} className="flex items-center px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">
            <Plus size={16} className="mr-2" /> Nouveau cas
          </button>
          <button onClick={() => refetch()} className="flex items-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</div>
            <div className="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mt-1">{stats.total}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-orange-200 dark:border-orange-800 p-4">
            <div className="text-xs font-medium text-orange-600 uppercase">En cours</div>
            <div className="text-3xl font-extrabold text-orange-600 mt-1">{stats.en_cours}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-800 p-4">
            <div className="text-xs font-medium text-red-600 uppercase">Sanctionnes</div>
            <div className="text-3xl font-extrabold text-red-600 mt-1">{stats.sanctionnes}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-green-200 dark:border-green-800 p-4">
            <div className="text-xs font-medium text-green-600 uppercase">Clos</div>
            <div className="text-3xl font-extrabold text-green-600 mt-1">{stats.clos}</div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="mb-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par nom, matricule, reference, matiere..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </div>
          </div>
          <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
            <option value="">Tous les statuts</option>
            {Object.entries(STATUT_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filtreType} onChange={(e) => setFiltreType(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
            <option value="">Tous les types</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-gray-400"><RefreshCw size={24} className="animate-spin mx-auto mb-2" /> Chargement...</div>
        ) : fraudes.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Shield size={32} className="mx-auto mb-2 opacity-50" />
            Aucun cas de fraude enregistre
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {fraudes.map(cas => {
              const typeInfo = TYPE_LABELS[cas.type_fraude] || TYPE_LABELS.autre;
              const statutInfo = STATUT_LABELS[cas.statut] || STATUT_LABELS.detecte;
              return (
                <div key={cas.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Ligne 1 : Reference + Statut + Type + Gravite */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-bold text-gray-900 dark:text-gray-100">{cas.reference}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statutInfo.color}`}>
                          {statutInfo.icon} {statutInfo.label}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${typeInfo.color}`}>{typeInfo.label}</span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${GRAVITE_COLORS[cas.gravite] || ''}`}>{cas.gravite_display}</span>
                      </div>
                      {/* Ligne 2 : Etudiant */}
                      <div className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                        <span className="font-semibold">{cas.etudiant_nom}</span>
                        <span className="text-gray-400 font-mono ml-2">{cas.etudiant_matricule}</span>
                        {cas.etudiant_classe && <span className="ml-2 px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-[10px]">{cas.etudiant_classe}</span>}
                      </div>
                      {/* Ligne 3 : Matiere + Date */}
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {cas.matiere_nom && <span>{cas.matiere_code} - {cas.matiere_nom} | </span>}
                        {new Date(cas.date_detection).toLocaleDateString('fr-FR')}
                        {cas.detecte_par_nom && <span> | par {cas.detecte_par_nom}</span>}
                        {cas.sanction_display && <span className="ml-2 text-red-600 font-medium">Sanction: {cas.sanction_display}</span>}
                      </div>
                      {/* Description */}
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{cas.description}</div>
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => { setSelectedCas(cas); setShowDetail(true); }} className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20" title="Voir detail">
                        <Eye size={16} />
                      </button>
                      <button onClick={() => handleDelete(cas)} className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 dark:hover:bg-red-900/20" title="Supprimer">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Formulaire */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Enregistrer un cas de fraude</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Etudiant */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Etudiant <span className="text-red-500">*</span></label>
                <input type="text" value={etudiantSearch} onChange={(e) => { setEtudiantSearch(e.target.value); setShowEtudiantDropdown(true); }}
                  placeholder="Rechercher par matricule ou nom..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                {showEtudiantDropdown && etudiantsFiltres.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-40 overflow-y-auto">
                    {etudiantsFiltres.map((e: any) => (
                      <button key={e.id} type="button" onClick={() => {
                        setForm(f => ({ ...f, etudiant: String(e.id) }));
                        setEtudiantSearch(`${e.matricule} - ${e.nom} ${e.prenoms} (${e.classe || ''})`);
                        setShowEtudiantDropdown(false);
                      }} className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20">
                        <span className="font-mono font-medium">{e.matricule}</span> - {e.nom} {e.prenoms}
                        {e.classe && <span className="ml-1 text-gray-400">({e.classe})</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type de fraude <span className="text-red-500">*</span></label>
                  <select value={form.type_fraude} onChange={(e) => setForm(f => ({ ...f, type_fraude: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gravite</label>
                  <select value={form.gravite} onChange={(e) => setForm(f => ({ ...f, gravite: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    <option value="mineure">Mineure</option>
                    <option value="moyenne">Moyenne</option>
                    <option value="grave">Grave</option>
                    <option value="tres_grave">Tres grave</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Matiere</label>
                  <input type="text" value={form.matiere_nom} onChange={(e) => setForm(f => ({ ...f, matiere_nom: e.target.value }))} placeholder="Nom de la matiere"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de l'examen</label>
                  <input type="date" value={form.date_examen} onChange={(e) => setForm(f => ({ ...f, date_examen: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                </div>
              </div>

              {/* Codes copies (double composition) */}
              {form.type_fraude === 'double_composition' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code copie 1</label>
                    <input type="text" value={form.code_copie_1} onChange={(e) => setForm(f => ({ ...f, code_copie_1: e.target.value }))} placeholder="SOR..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code copie 2</label>
                    <input type="text" value={form.code_copie_2} onChange={(e) => setForm(f => ({ ...f, code_copie_2: e.target.value }))} placeholder="SOR..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description <span className="text-red-500">*</span></label>
                <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Decrivez le cas de fraude..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Circonstances</label>
                <textarea value={form.circonstances} onChange={(e) => setForm(f => ({ ...f, circonstances: e.target.value }))} rows={2} placeholder="Circonstances de la decouverte..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes internes</label>
                <textarea value={form.notes_internes} onChange={(e) => setForm(f => ({ ...f, notes_internes: e.target.value }))} rows={2} placeholder="Notes internes (non visibles par l'etudiant)..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>

              {/* Preuves numériques */}
              <div className="border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <ImageIcon size={14} className="text-blue-600" />
                    Preuves numériques ({preuvesAUploader.length})
                  </label>
                </div>

                {/* Liste des preuves attachées */}
                {preuvesAUploader.length > 0 && (
                  <div className="space-y-1.5 mb-2">
                    {preuvesAUploader.map((p, idx) => {
                      const isImage = p.file.type.startsWith('image/');
                      return (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                          {isImage ? (
                            <img src={URL.createObjectURL(p.file)} alt="" className="w-10 h-10 object-cover rounded" />
                          ) : (
                            <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center">
                              <FileText size={16} className="text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <input
                              type="text"
                              value={p.titre}
                              onChange={(e) => {
                                const next = [...preuvesAUploader];
                                next[idx] = { ...next[idx], titre: e.target.value };
                                setPreuvesAUploader(next);
                              }}
                              placeholder={p.file.name}
                              className="w-full text-xs px-1.5 py-1 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                            <div className="text-[10px] text-gray-400 mt-0.5 truncate">{p.file.name} · {(p.file.size / 1024).toFixed(0)} Ko</div>
                          </div>
                          <select
                            value={p.type}
                            onChange={(e) => {
                              const next = [...preuvesAUploader];
                              next[idx] = { ...next[idx], type: e.target.value };
                              setPreuvesAUploader(next);
                            }}
                            className="text-[10px] px-1.5 py-1 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          >
                            <option value="copie_scannee">Copie scannée</option>
                            <option value="photo">Photo</option>
                            <option value="document">Document</option>
                            <option value="autre">Autre</option>
                          </select>
                          <button
                            onClick={() => setPreuvesAUploader(preuvesAUploader.filter((_, i) => i !== idx))}
                            className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            title="Retirer"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <input
                  ref={formFileInputRef}
                  type="file"
                  accept="image/*,application/pdf,.heic"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    const newPreuves = files
                      .filter(f => f.size <= 10 * 1024 * 1024)
                      .map(f => ({ file: f, type: 'copie_scannee', titre: '' }));
                    if (files.length !== newPreuves.length) {
                      alert('Certains fichiers dépassent 10 Mo et ont été ignorés');
                    }
                    setPreuvesAUploader([...preuvesAUploader, ...newPreuves]);
                    if (formFileInputRef.current) formFileInputRef.current.value = '';
                  }}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => formFileInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-dashed border-blue-400 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-xs font-medium"
                >
                  <Upload size={14} />
                  Ajouter des preuves (images, PDF — max 10 Mo)
                </button>
                <p className="text-[10px] text-gray-500 mt-1">Les preuves seront uploadées après l'enregistrement du cas</p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
                <button onClick={() => { setShowForm(false); setPreuvesAUploader([]); }} className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300">Annuler</button>
                <button onClick={handleCreate} disabled={creating || uploadingPreuve}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                  {creating ? 'Enregistrement...' : uploadingPreuve ? 'Upload preuves...' : `Enregistrer le cas${preuvesAUploader.length > 0 ? ` + ${preuvesAUploader.length} preuve(s)` : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detail */}
      {showDetail && selectedCas && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{selectedCas.reference}</h3>
                <p className="text-sm text-gray-500">{selectedCas.type_fraude_display} - {selectedCas.gravite_display}</p>
              </div>
              <button onClick={() => setShowDetail(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Etudiant */}
              <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{selectedCas.etudiant_nom}</div>
                    <div className="text-xs text-gray-500 font-mono">{selectedCas.etudiant_matricule} {selectedCas.etudiant_classe && `| ${selectedCas.etudiant_classe}`}</div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${STATUT_LABELS[selectedCas.statut]?.color}`}>
                    {STATUT_LABELS[selectedCas.statut]?.label}
                  </span>
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Matiere:</span> <span className="font-medium">{selectedCas.matiere_nom || '—'}</span></div>
                <div><span className="text-gray-500">Date detection:</span> <span className="font-medium">{new Date(selectedCas.date_detection).toLocaleDateString('fr-FR')}</span></div>
                <div><span className="text-gray-500">Detecte par:</span> <span className="font-medium">{selectedCas.detecte_par_nom || '—'}</span></div>
                <div><span className="text-gray-500">Traite par:</span> <span className="font-medium">{selectedCas.traite_par_nom || '—'}</span></div>
                {selectedCas.code_copie_1 && <div><span className="text-gray-500">Copie 1:</span> <span className="font-mono font-medium">{selectedCas.code_copie_1}</span></div>}
                {selectedCas.code_copie_2 && <div><span className="text-gray-500">Copie 2:</span> <span className="font-mono font-medium">{selectedCas.code_copie_2}</span></div>}
                {selectedCas.sanction_display && <div className="col-span-2"><span className="text-gray-500">Sanction:</span> <span className="font-medium text-red-600">{selectedCas.sanction_display}</span></div>}
              </div>

              <div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 rounded p-3">{selectedCas.description}</div>
              </div>

              {selectedCas.circonstances && (
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Circonstances</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 rounded p-3">{selectedCas.circonstances}</div>
                </div>
              )}

              {selectedCas.notes_internes && (
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes internes</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded p-3">{selectedCas.notes_internes}</div>
                </div>
              )}

              {/* Preuves numériques */}
              <div className="border-t dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <ImageIcon size={16} className="text-blue-600" />
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Preuves numériques ({selectedCas.preuves?.length || 0})
                    </span>
                  </div>
                </div>

                {/* Galerie des preuves */}
                {selectedCas.preuves && selectedCas.preuves.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    {selectedCas.preuves.map(p => {
                      const isImage = /\.(jpg|jpeg|png|gif|webp|heic)$/i.test(p.fichier);
                      const isPdf = /\.pdf$/i.test(p.fichier);
                      return (
                        <div key={p.id} className="relative group border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-900">
                          {isImage ? (
                            <div
                              className="aspect-square cursor-pointer overflow-hidden"
                              onClick={() => setPreviewImage(p.fichier_url)}
                            >
                              <img
                                src={p.fichier_url}
                                alt={p.titre}
                                className="w-full h-full object-cover hover:scale-105 transition-transform"
                              />
                            </div>
                          ) : (
                            <div className="aspect-square flex flex-col items-center justify-center text-gray-400 p-2">
                              <FileText size={32} />
                              <span className="text-[10px] mt-1">{isPdf ? 'PDF' : 'Document'}</span>
                            </div>
                          )}
                          {/* Overlay info */}
                          <div className="p-2 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                            <div className="text-[10px] font-semibold text-gray-900 dark:text-gray-100 truncate" title={p.titre}>
                              {p.titre || 'Sans titre'}
                            </div>
                            <div className="text-[9px] text-gray-500">{p.type_preuve_display}</div>
                            <div className="text-[9px] text-gray-400 mt-0.5">
                              {new Date(p.date_ajout).toLocaleDateString('fr-FR')} {p.ajoute_par_nom && `· ${p.ajoute_par_nom}`}
                            </div>
                          </div>
                          {/* Actions */}
                          <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <a
                              href={p.fichier_url}
                              download
                              className="p-1 bg-white/90 dark:bg-gray-800/90 rounded shadow text-blue-600 hover:bg-blue-50"
                              title="Télécharger"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Download size={12} />
                            </a>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeletePreuve(p.id); }}
                              className="p-1 bg-white/90 dark:bg-gray-800/90 rounded shadow text-red-600 hover:bg-red-50"
                              title="Supprimer"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Upload nouvelle preuve */}
                <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={preuveType}
                      onChange={(e) => setPreuveType(e.target.value)}
                      className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="copie_scannee">Copie scannée</option>
                      <option value="photo">Photographie</option>
                      <option value="document">Document externe</option>
                      <option value="autre">Autre</option>
                    </select>
                    <input
                      type="text"
                      value={preuveTitre}
                      onChange={(e) => setPreuveTitre(e.target.value)}
                      placeholder="Titre / légende (optionnel)"
                      className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf,.heic"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleUploadPreuve(file);
                    }}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPreuve}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium disabled:opacity-50"
                  >
                    {uploadingPreuve ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploadingPreuve ? 'Upload en cours...' : 'Ajouter une preuve (image, PDF, max 10 Mo)'}
                  </button>
                </div>
              </div>

              {/* Historique */}
              {selectedCas.historique && selectedCas.historique.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Historique</div>
                  <div className="space-y-1">
                    {selectedCas.historique.map(h => (
                      <div key={h.id} className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="text-gray-400">{new Date(h.date_action).toLocaleDateString('fr-FR')}</span>
                        <span>{h.ancien_statut ? `${h.ancien_statut} → ` : ''}{h.nouveau_statut}</span>
                        {h.commentaire && <span className="italic">— {h.commentaire}</span>}
                        {h.effectue_par_nom && <span className="text-gray-400">par {h.effectue_par_nom}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions de changement de statut */}
              <div className="flex flex-wrap gap-2 pt-4 border-t dark:border-gray-700">
                <span className="text-xs text-gray-500 self-center mr-2">Changer statut :</span>
                {Object.entries(STATUT_LABELS).filter(([k]) => k !== selectedCas.statut).map(([k, v]) => (
                  <button key={k} onClick={() => { handleChangerStatut(selectedCas, k); setShowDetail(false); }}
                    className={`px-2 py-1 rounded text-[10px] font-medium ${v.color} hover:opacity-80`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal preview image plein écran */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
          >
            <X size={24} />
          </button>
          <a
            href={previewImage}
            download
            onClick={(e) => e.stopPropagation()}
            className="absolute top-4 right-16 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
            title="Télécharger"
          >
            <Download size={20} />
          </a>
          <img
            src={previewImage}
            alt="Preuve"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default GestionFraudes;
